import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { SORT_OPTIONS_MAP } from "@circle-react/hooks/useCommunityMembersInfiniteQueryV2";
import { withSearchDelay } from "@circle-react/hooks/utils/withSearchDelay";
import type { CommunityMember } from "@circle-react/types";
import { useAsyncSearch } from "@circleco/compass/hooks/useAsyncSearch";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import {
  buildCommunityMemberOptions,
  type CommunityMemberOption,
} from "./useCommunityMemberSelectOptions";

interface UseCommunityMemberContainsSearchParams {
  excludeAdmins?: boolean;
  filters?: Record<string, unknown>;
  getCommunityMemberText?: (member: CommunityMember) => string;
  includeInactive?: boolean;
  perPage?: number;
  valueField?: "id" | "user_id";
}

// Substring-matching member search, used by surfaces that opt in with
// searchMode="contains". The v1 search that useCommunityMemberSelectAsyncSearch
// calls matches names by word prefix and emails only in full, so those surfaces
// would otherwise lose partial-email lookup.
//
// Delete this alongside useCommunityMemberSelectAsyncSearch once
// crm_clickhouse_contacts_search_pickers_enabled is on everywhere — the CRM
// variant already matches substrings.
export const useCommunityMemberContainsSearch = ({
  excludeAdmins = false,
  filters,
  getCommunityMemberText,
  includeInactive = false,
  perPage = 10,
  valueField = "id",
}: UseCommunityMemberContainsSearchParams) => {
  const queryClient = useQueryClient();
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const [isOpen, setIsOpen] = useState(false);

  const additionalFilters = useMemo(
    () =>
      Object.entries(filters ?? {})
        .filter(([key]) => key !== "exclude_ids")
        .map(([key, value]) => ({ key, value })),
    [filters],
  );

  const filtersKey = useMemo(
    () => JSON.stringify(additionalFilters),
    [additionalFilters],
  );

  const excludedIds = useMemo(
    () =>
      new Set(
        (Array.isArray(filters?.exclude_ids) ? filters.exclude_ids : []).map(
          String,
        ),
      ),
    [filters],
  );

  const search = useCallback(
    async (query: string, signal: AbortSignal) => {
      const records = await queryClient.fetchQuery({
        queryKey: [
          "community-members",
          "contains-search",
          query,
          excludeAdmins,
          includeInactive,
          filtersKey,
          perPage,
        ],
        queryFn: async () => {
          const response = await reactQueryGet<{
            records: CommunityMember[];
          }>(
            internalApi.search.communityMembers.indexV2({
              params: {
                page: 1,
                sort: SORT_OPTIONS_MAP.ALPHABETICAL,
                per_page: perPage,
                // Spread-or-nothing, never `status: undefined`. See the same
                // guard in useCommunityMemberCRMSearch — both paths must omit
                // the key identically or the feature flag changes which
                // members are searchable, not just where they come from.
                ...(includeInactive ? { status: "all" } : {}),
                filters: [
                  {
                    key: "name_and_email",
                    value: query,
                    filter_type: "contains",
                  },
                  ...additionalFilters,
                ],
              },
            }),
            { signal },
          );
          return response.records;
        },
        staleTime: 3 * 60 * 1000,
      });

      if (signal.aborted) return [];

      const visibleRecords = records.filter(member => {
        const isExcluded = excludedIds.has(
          String(member[valueField] ?? member.id),
        );
        // The v2 filter builder has no exclude_admins param, so mirror the CRM
        // hook and drop admins here.
        const isExcludedAdmin =
          excludeAdmins &&
          (member.roles?.admin || member.admin || member.is_admin);
        return !isExcluded && !isExcludedAdmin;
      });

      return buildCommunityMemberOptions({
        communityMembers: visibleRecords,
        getCommunityMemberText,
        valueField,
        isAdmin,
      });
    },
    [
      queryClient,
      additionalFilters,
      excludeAdmins,
      excludedIds,
      filtersKey,
      getCommunityMemberText,
      includeInactive,
      isAdmin,
      perPage,
      valueField,
    ],
  );

  const debouncedSearch = useMemo(() => withSearchDelay(search), [search]);

  const onSearch = useCallback(
    (query: string, signal: AbortSignal) =>
      query.trim() ? debouncedSearch(query, signal) : Promise.resolve([]),
    [debouncedSearch],
  );

  const { searchProps } = useAsyncSearch<CommunityMemberOption>({ onSearch });

  const handleInputValueChange: typeof searchProps.onInputValueChange = (
    query,
    details,
  ) => {
    if (
      ["input-change", "input-paste", "input-clear"].includes(details.reason)
    ) {
      setIsOpen(query.trim().length > 0);
    }
    searchProps.onInputValueChange(query, details);
  };

  return {
    isOpen,
    setIsOpen,
    searchProps: { ...searchProps, onInputValueChange: handleInputValueChange },
  };
};
